"use client";
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, BookOpen, ChevronRight, Star } from 'lucide-react';
import { BlogPost } from '@/lib/blogData';

interface BlogPostWithExtra extends BlogPost {
  isSanity?: boolean;
  coverImageUrl?: string;
}

interface BlogsClientProps {
  posts: BlogPostWithExtra[];
}

const BlogsClient: React.FC<BlogsClientProps> = ({ posts }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const blogPosts = posts;

  // Category icons as SVG line-art (Ubersuggest style - black stroke, no fill)
  const categories = [
    {
      name: "City Guides",
      slug: "City Guides",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="8" y="12" width="48" height="40" rx="3" />
          <path d="M8 22h48" />
          <circle cx="16" cy="17" r="2" />
          <circle cx="22" cy="17" r="2" />
          <circle cx="28" cy="17" r="2" />
          <path d="M20 32l8-4 8 4 8-4" />
          <circle cx="24" cy="38" r="3" />
          <path d="M24 35v-2" />
        </svg>
      ),
    },
    {
      name: "Safety Tips",
      slug: "Safety Tips",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M32 8l20 8v16c0 12-8 20-20 24C20 52 12 44 12 32V16l20-8z" />
          <path d="M24 32l5 5 11-11" />
        </svg>
      ),
    },
    {
      name: "Guides",
      slug: "Guides",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="10" y="8" width="44" height="48" rx="3" />
          <path d="M20 20h24M20 28h24M20 36h16" />
          <path d="M40 36l4 4 8-8" />
        </svg>
      ),
    },
    {
      name: "Weekend Rides",
      slug: "Weekend Rides",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="20" cy="40" r="10" />
          <circle cx="44" cy="40" r="10" />
          <path d="M20 40l8-16h8l4 8h4" />
          <circle cx="20" cy="40" r="3" />
          <circle cx="44" cy="40" r="3" />
        </svg>
      ),
    },
    {
      name: "Bike Selection",
      slug: "Bike Selection",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 48c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10-4.5 10-10 10z" />
          <path d="M48 48c-5.5 0-10-4.5-10-10s4.5-10 10-10 10 4.5 10 10-4.5 10-10 10z" />
          <path d="M16 38h16l8-16h12" />
          <path d="M36 22l-4 16" />
          <path d="M44 22h8" />
        </svg>
      ),
    },
    {
      name: "Tips & Tricks",
      slug: "Tips & Tricks",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="32" cy="26" r="14" />
          <path d="M24 36v6a8 8 0 0016 0v-6" />
          <path d="M26 48h12" />
          <path d="M26 52h12" />
          <path d="M32 12v-4M18 14l-2-3M46 14l2-3M52 26h4M8 26h4" />
        </svg>
      ),
    },
    {
      name: "Pricing & Plans",
      slug: "Pricing",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="8" y="14" width="48" height="36" rx="4" />
          <path d="M8 26h48" />
          <path d="M20 36h8" />
          <path d="M20 42h12" />
          <circle cx="46" cy="39" r="5" />
          <path d="M46 36v6M43 39h6" />
        </svg>
      ),
    },
    {
      name: "FAQs",
      slug: "FAQs",
      icon: (
        <svg className="w-12 h-12 md:w-14 md:h-14" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="32" cy="32" r="22" />
          <path d="M24 24c0-4.4 3.6-8 8-8s8 3.6 8 8c0 4-4 5-4 8h-8c0-3 4-4 4-8" />
          <circle cx="32" cy="44" r="2" fill="currentColor" />
        </svg>
      ),
    },
  ];

  // Search results
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return blogPosts.filter(
      (post) =>
        post.title.toLowerCase().includes(term) ||
        post.excerpt.toLowerCase().includes(term) ||
        post.tags.some((tag) => tag.toLowerCase().includes(term))
    );
  }, [searchTerm, blogPosts]);

  // Featured / promoted posts
  const featuredPosts = blogPosts.filter((p) => p.featured);
  const recentPosts = blogPosts.slice(0, 9);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner - Ubersuggest exact style */}
      <section className="relative overflow-hidden">
        {/* Background image with orange overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497032628192-86f99bcd76bc?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/85 via-orange-500/80 to-amber-500/75" />
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-32 md:pb-40">
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-10">
            What can we help you with?
          </h1>

          {/* Search Bar - exact Ubersuggest style (separate input + button) */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative flex gap-3">
            <div className={`flex-1 flex bg-white rounded-md shadow-xl overflow-hidden transition-all duration-300 ${isSearchFocused ? 'ring-4 ring-white/30' : ''}`}>
              <div className="flex items-center pl-5 pr-2">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Have a question? Ask or enter a search term."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                className="flex-1 py-4 md:py-5 px-2 text-gray-700 placeholder-gray-400 focus:outline-none text-sm md:text-base"
              />
            </div>
            <button
              type="submit"
              className="bg-white text-orange-500 px-8 md:px-10 rounded-md font-semibold hover:bg-gray-50 transition-colors text-sm md:text-base shadow-xl border border-gray-100"
            >
              Search
            </button>

            {/* Search Results Dropdown */}
            {searchTerm.trim() && isSearchFocused && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-80 overflow-y-auto">
                {searchResults.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No articles found for &quot;{searchTerm}&quot;</p>
                  </div>
                ) : (
                  searchResults.map((post) => (
                    <Link
                      key={post.id}
                      href={`/blogs/${post.slug}`}
                      className="flex items-start gap-3 p-4 hover:bg-orange-50 transition-colors border-b border-gray-50 last:border-0"
                    >
                      <BookOpen className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                      <div className="text-left">
                        <h4 className="font-medium text-gray-800 text-sm">{post.title}</h4>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-1">{post.excerpt}</p>
                      </div>
                    </Link>
                  ))
                )}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Category Cards - 4 column white cards overlapping banner */}
      <section className="relative z-10 -mt-16 md:-mt-20 px-4 sm:px-6 lg:px-8 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {categories.map((category) => (
              <button
                key={category.slug}
                onClick={() => {
                  const section = document.getElementById(`category-${category.slug}`);
                  if (section) section.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-6 md:p-8 text-center group border border-gray-100"
              >
                <div className="text-gray-800 mx-auto mb-4 group-hover:scale-110 transition-transform inline-block">
                  {category.icon}
                </div>
                <h3 className="font-bold text-gray-800 text-sm md:text-base">
                  {category.name}
                </h3>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Section */}
      {featuredPosts.length > 0 && (
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-10">
              Resources
            </h2>
            <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {featuredPosts.map((post) => (
                <Link
                  key={post.id}
                  href={`/blogs/${post.slug}`}
                  className="group bg-orange-50 border border-orange-100 rounded-lg p-8 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="text-orange-500 mx-auto mb-3 inline-block">
                    <Star className="w-10 h-10" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg group-hover:text-orange-600 transition-colors">
                    {post.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Promoted Articles - 3 column link list (exact Ubersuggest style) */}
      <section className="py-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 text-center mb-12">
            Promoted articles
          </h2>

          <div className="grid md:grid-cols-3 gap-x-10 gap-y-8">
            {recentPosts.map((post) => (
              <Link
                key={post.id}
                href={`/blogs/${post.slug}`}
                className="group block"
              >
                <div className="flex items-start gap-2">
                  <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-orange-500 group-hover:text-orange-600 transition-colors leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1.5 line-clamp-2 leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Category Sections (scrollable targets) */}
      {Object.entries(
        blogPosts.reduce((acc, post) => {
          if (!acc[post.category]) acc[post.category] = [];
          acc[post.category].push(post);
          return acc;
        }, {} as Record<string, typeof blogPosts>)
      ).map(([category, posts]) => (
        <section
          key={category}
          id={`category-${category}`}
          className="py-10 px-4 sm:px-6 lg:px-8 even:bg-gray-50 scroll-mt-20"
        >
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
              {category}
            </h2>
            <div className="grid md:grid-cols-3 gap-x-10 gap-y-5">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  href={`/blogs/${post.slug}`}
                  className="group flex items-start gap-2"
                >
                  <ChevronRight className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-orange-500 group-hover:text-orange-600 transition-colors leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2 leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Footer - Ubersuggest wavy orange style */}
      <section className="relative overflow-hidden">
        {/* Wavy top edge */}
        <div className="absolute top-0 left-0 w-full">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
            <path d="M0 120V60C240 20 480 0 720 20C960 40 1200 80 1440 60V120H0Z" fill="#ea580c" fillOpacity="0.15" />
            <path d="M0 120V80C240 40 480 20 720 40C960 60 1200 100 1440 80V120H0Z" fill="#ea580c" fillOpacity="0.3" />
            <path d="M0 120V100C240 70 480 50 720 70C960 90 1200 110 1440 100V120H0Z" fill="#ea580c" />
          </svg>
        </div>
        <div className="bg-[#ea580c] pt-20 pb-16 text-center px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Can&apos;t find what you&apos;re looking for?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Let us help you!
          </p>
          <Link
            href="/contact-us"
            className="inline-block bg-white text-orange-600 px-8 py-3.5 rounded-md font-semibold hover:bg-gray-50 transition-colors border border-white shadow-lg"
          >
            Submit a request
          </Link>
        </div>
      </section>

      <style>{`
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default BlogsClient;
